import React, { forwardRef, useEffect, useState } from 'react';
import styles from './JuryProjectCard.module.scss';
import {
    useJuryStore,
    useJuryFundingOptions,
    useJuryPointsConfiguration,
    useJuryVotingType,
} from '@core/frontend/stores';
import CardActionButton from './CardActionButton';
import { IconStar, IconStarFilled, IconCircleCheck, IconAward, IconAwardFilled } from '@tabler/icons-react';
import JuryProjectDetails from '../JuryProjectDetails/JuryProjectDetails';
import JuryCategoryBadge from '../../JuryCategoryBadge/JuryCategoryBadge';
import { mergeClassNames } from '@core/shared-utils';
import {
    clampPoints,
    countByAmount,
    maxPointsForProject,
    nextIncrementValue,
    resolveFundingOptionAvailability,
    sumPoints,
    takenAmountsForProject,
} from '@core/business-logic';

/**
 * @param project           Project data
 * @param isSelected        true when this card is the currently-selected card
 *                          in the list (highlights the border)
 * @param onSelect          called when the card body is clicked
 * @param showInlineDetails true on mobile, where the details accordion lives
 *                          inside the card itself. Desktop renders details
 *                          in a separate right-hand pane and passes false.
 */
const JuryProjectCard = forwardRef(({ id, project, isSelected = false, onSelect, showInlineDetails = false }, ref) => {
    const votingType = useJuryVotingType();
    const isPointsMode = votingType === 'points';
    const pointsConfig = useJuryPointsConfiguration();

    const assignedFunding = useJuryStore((s) => s.poll[project._id] ?? null);
    const isAssignedFunding = assignedFunding !== null;
    // `undefined` means "not rated" — 0 is a valid, explicit rating
    const assignedPoints = useJuryStore((s) => s.points[project._id]);
    const isRated = assignedPoints !== undefined;
    const isBookmarked = useJuryStore((s) => s.bookmarks.has(project._id));
    const assignFunding = useJuryStore((s) => s.assignFunding);
    const setPoints = useJuryStore((s) => s.setPoints);
    const toggleBookmark = useJuryStore((s) => s.toggleBookmark);
    const isConfirmed = useJuryStore((s) => s.isConfirmed);
    const fundingOptions = useJuryFundingOptions();
    const poll = useJuryStore((s) => s.poll);
    const pointsByProject = useJuryStore((s) => s.points);

    // Text being typed into the points input; only committed on blur/Enter
    const [pointsDraft, setPointsDraft] = useState(isRated ? String(assignedPoints) : '');

    useEffect(() => {
        setPointsDraft(isRated ? String(assignedPoints) : '');
    }, [isRated, assignedPoints]);

    // Amounts assigned to OTHER projects, and the free slots left per amount
    const takenCountByAmount = countByAmount(takenAmountsForProject(poll, project._id));
    const optionsWithAvailability = resolveFundingOptionAvailability(fundingOptions, takenCountByAmount);

    const minPoints = pointsConfig.minPointsPerProject;
    const maxPoints = pointsConfig.maxPointsPerProject;

    // Points already given to the other projects, needed for the global budget
    const pointsOfOtherProjectsExcludingThis = sumPoints(pointsByProject) - (isRated ? assignedPoints : 0);
    const projectMaxPoints = maxPointsForProject(pointsConfig, pointsOfOtherProjectsExcludingThis);
    const projectNextIncrement = nextIncrementValue(minPoints, isRated ? assignedPoints : undefined);
    // From the unrated state the minus button jumps straight to the lowest allowed value,
    // so jurors do not have to increment first to reach 0.
    const isDecrementDisabled = isConfirmed || (isRated && assignedPoints <= minPoints);
    const isIncrementDisabled = isConfirmed || projectNextIncrement > projectMaxPoints;

    const thumbnail = project.projectGallery?.[0];

    const handleBookmark = () => toggleBookmark(project._id);

    const handleSelect = () => {
        if (onSelect) onSelect(project);
    };

    const handleSelectChange = (e) => {
        const val = e.target.value;
        if (val === '__remove__') {
            assignFunding(project._id, null);
        } else {
            assignFunding(project._id, val ? Number(val) : null);
        }
    };

    /**
     * Applies the typed value: clamped to the allowed range, rounded to an
     * integer, `null` when the field was emptied. Invalid input restores the
     * stored value instead of silently discarding it.
     */
    const commitPoints = (rawValue) => {
        const trimmed = String(rawValue).trim();
        if (trimmed === '') {
            setPointsDraft('');
            if (isRated) setPoints(project._id, null);
            return;
        }

        const parsed = Number.parseInt(trimmed, 10);
        if (!Number.isInteger(parsed)) {
            setPointsDraft(isRated ? String(assignedPoints) : '');
            return;
        }

        const clamped = clampPoints(parsed, minPoints, projectMaxPoints);

        // Without remaining budget only the lower bound could be assigned. A higher value must
        // not be silently stored as a 0-point rating.
        if (!isRated && parsed > minPoints && clamped === minPoints) {
            setPointsDraft('');
            return;
        }

        setPointsDraft(String(clamped));
        if (clamped !== assignedPoints) setPoints(project._id, clamped);
    };

    const handlePointsKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            commitPoints(pointsDraft);
        } else if (e.key === 'Escape') {
            setPointsDraft(isRated ? String(assignedPoints) : '');
        }
    };

    const handleIncrement = () => {
        if (isIncrementDisabled) return;
        setPointsDraft(String(projectNextIncrement));
        setPoints(project._id, projectNextIncrement);
    };

    const handleDecrement = () => {
        if (isDecrementDisabled) return;
        const nextValue = isRated ? assignedPoints - 1 : minPoints;
        setPointsDraft(String(nextValue));
        setPoints(project._id, nextValue);
    };

    const cardClasses = mergeClassNames(styles.ProjectCard, isSelected ? styles.ProjectCardSelected : '');
    const inputGroupClasses = mergeClassNames('input-group', isPointsMode ? styles.PointsStepper : '');
    const pointsInputId = `project-${project._id}-points`;
    const pointsStatusId = `project-${project._id}-points-status`;

    return (
        <div id={id} ref={ref} className={cardClasses}>
            <button type="button" className={styles.ProjectCardLink} aria-pressed={isSelected} onClick={handleSelect}>
                <div className={styles.CardTop}>
                    <div className={styles.ImageColumn}>
                        <div className={styles.ImageSection}>
                            {thumbnail ? (
                                <img
                                    src={thumbnail.url}
                                    alt={thumbnail.alt || project.contestantName}
                                    className={styles.ProjectImage}
                                />
                            ) : (
                                <div className={styles.NoImage} />
                            )}
                            <div className={styles.VoteBadge}>
                                <IconCircleCheck className={styles.VoteIcon} aria-hidden="true" />
                                {(project.voteCount || 0).toLocaleString('de-DE')} Stimmen
                            </div>
                        </div>
                        {project.voteCountFinale !== undefined && project.voteCountFinale !== null && (
                            <div className={styles.VoteBadgeFinale}>
                                <IconCircleCheck className={styles.VoteIcon} aria-hidden="true" />
                                {project.voteCountFinale.toLocaleString('de-DE')} im Finale
                            </div>
                        )}
                    </div>
                    <div className={styles.InstitutionSection}>
                        <h2 className={styles.InstitutionName}>{project.contestantName}</h2>
                    </div>
                </div>

                {project.projectTitle && (
                    <div className={styles.TitleSection}>
                        <p className={styles.ProjectTitle}>{project.projectTitle}</p>
                    </div>
                )}

                <ul className={styles.TagsSection} aria-label="Kategorien">
                    {[...(project.badges || [])]
                        .sort((a, b) => {
                            const groupCompare = a.group.localeCompare(b.group);
                            if (groupCompare !== 0) return groupCompare;
                            return a.value.localeCompare(b.value);
                        })
                        .map((badge) => (
                            <JuryCategoryBadge
                                key={badge._id}
                                component="li"
                                size="sm"
                                label={badge.value}
                                color={badge.color}
                            />
                        ))}
                </ul>
            </button>

            {showInlineDetails && (
                <button
                    type="button"
                    className={styles.ToggleDetailsButton}
                    onClick={handleSelect}
                    aria-expanded={isSelected}
                >
                    {isSelected ? 'Projektdetails ausblenden -' : 'Projektdetails anzeigen +'}
                </button>
            )}

            {showInlineDetails && isSelected && (
                <div className={styles.InlineDetails}>
                    <JuryProjectDetails project={project} />
                    <button type="button" className={styles.ToggleDetailsButtonBottom} onClick={handleSelect}>
                        Projektdetails ausblenden -
                    </button>
                </div>
            )}

            {/* Actions Section */}
            <div className={styles.ActionsSection}>
                <div className={inputGroupClasses}>
                    {!isPointsMode && (
                        <span
                            className={`input-group-text pe-0 border-end-0 ${isConfirmed ? styles.PrizeSelectMedalDisabled : 'bg-white'}`}
                            aria-hidden="true"
                        >
                            {isAssignedFunding ? (
                                <IconAwardFilled size="1.625rem" className={styles.PrizeSelectMedalActive} />
                            ) : (
                                <IconAward size="1.625rem" className={styles.PrizeSelectMedalInactive} />
                            )}
                        </span>
                    )}

                    {isPointsMode ? (
                        <>
                            <button
                                type="button"
                                className={`btn btn-outline-secondary ${styles.StepperButton}`}
                                onClick={handleDecrement}
                                disabled={isDecrementDisabled}
                                aria-label={`Punkte für ${project.contestantName} verringern`}
                            >
                                −
                            </button>
                            <input
                                id={pointsInputId}
                                name={pointsInputId}
                                type="number"
                                inputMode="numeric"
                                step={1}
                                min={minPoints}
                                max={maxPoints}
                                className={`form-control text-center ${styles.PointsInput}`}
                                value={pointsDraft}
                                placeholder="–"
                                disabled={isConfirmed}
                                aria-label={`Punkte für ${project.contestantName} (${minPoints} bis ${maxPoints})`}
                                aria-describedby={pointsStatusId}
                                onChange={(e) => setPointsDraft(e.target.value)}
                                onBlur={() => commitPoints(pointsDraft)}
                                onKeyDown={handlePointsKeyDown}
                            />
                            <button
                                type="button"
                                className={`btn btn-outline-secondary ${styles.StepperButton}`}
                                onClick={handleIncrement}
                                disabled={isIncrementDisabled}
                                aria-label={`Punkte für ${project.contestantName} erhöhen`}
                            >
                                +
                            </button>
                        </>
                    ) : (
                        <select
                            className={`form-select border-start-0 ${styles.PrizeSelect}`}
                            value={assignedFunding !== null ? String(assignedFunding) : ''}
                            disabled={isConfirmed}
                            aria-label="Förderung vergeben"
                            onChange={handleSelectChange}
                            id={`project-${project._id}-funding`}
                            name={`project-${project._id}-funding`}
                        >
                            <option value="" disabled>
                                Förderung vergeben…
                            </option>
                            <option value="__remove__" disabled={assignedFunding === null}>
                                Förderung entfernen
                            </option>
                            {optionsWithAvailability.map(({ idx, opt, disabled }) => (
                                <option key={idx} value={String(opt.amount)} disabled={disabled}>
                                    {opt.displayValue}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {isPointsMode && (
                    <span id={pointsStatusId} className="visually-hidden">
                        {isRated
                            ? `${project.contestantName}: ${assignedPoints} von ${maxPoints} Punkten bewertet`
                            : `${project.contestantName}: noch nicht bewertet`}
                    </span>
                )}

                <CardActionButton
                    icon={
                        isBookmarked ? (
                            <IconStarFilled size="1.5rem" className={styles.BookmarkIconActive} aria-hidden="true" />
                        ) : (
                            <IconStar size="1.5rem" className={styles.BookmarkIconInactive} aria-hidden="true" />
                        )
                    }
                    label="Merken"
                    onClick={handleBookmark}
                    ariaLabel={isBookmarked ? 'Von Merkliste entfernen' : 'Zur Merkliste hinzufügen'}
                    disabled={isConfirmed}
                />
            </div>
        </div>
    );
});

export default JuryProjectCard;
